import React, { useState, useEffect } from 'react';

/**
 * BuildIdLine — tiny "Build #N · sha" footer line shown to all
 * users (no super-admin gate). Useful for support triage: a user
 * reporting a bug can read this number off the sidebar without
 * needing admin access.
 *
 * Source: GET /api/build-info — the public, no-auth counterpart
 * to the super-admin /api/admin/build-info endpoint. Returns only
 * the build identity (number, short SHA, timestamp) — never any
 * platform / runtime metadata. Fails silently if the endpoint is
 * unreachable so the sidebar never breaks on a transient error.
 */
export default function BuildIdLine() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    if (!apiBase) return undefined;
    fetch(`${apiBase}/api/build-info`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled && data) setInfo(data); })
      .catch(() => { /* swallow — don't break the sidebar */ });
    return () => { cancelled = true; };
  }, []);

  if (!info || !info.build_number) return null;
  const shortSha = (info.git_commit || '').slice(0, 7);
  return (
    <p
      className="mt-1 text-[10px] text-slate-500 font-mono tabular-nums"
      title={info.built_at ? `Built ${info.built_at}` : 'Build identity'}
      data-testid="sidebar-build-id"
    >
      Build #{info.build_number}{shortSha ? ` · ${shortSha}` : ''}
    </p>
  );
}
